import React, { ReactNode } from 'react';
import { Box, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { useAuthenticatedUser, AuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import {
  Permission,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
} from '../utils/permissions';

interface PermissionGuardProps {
  children: ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean; // If true, user must have ALL permissions; if false, user needs ANY permission
  fallback?: ReactNode;
  showFallback?: boolean;
  user?: AuthenticatedUser | null; // Optional: override the user from context
}

// Main Permission Guard Component
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  showFallback = false,
  user: overrideUser,
}) => {
  const { authenticatedUser } = useAuthenticatedUser();
  const user = overrideUser ?? authenticatedUser;

  // Check permissions
  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(user, permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll
      ? hasAllPermissions(user, permissions)
      : hasAnyPermission(user, permissions);
  } else {
    // If no permissions specified, allow access
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  if (showFallback && fallback) {
    return <>{fallback}</>;
  }

  if (showFallback) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Access Restricted</AlertTitle>
          <AlertDescription>You don't have permission to view this content.</AlertDescription>
        </Box>
      </Alert>
    );
  }

  return null;
};

// Role-based guard (simplified for specific roles)
interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: Array<'admin' | 'editor' | 'viewer'>;
  fallback?: ReactNode;
  showFallback?: boolean;
  user?: AuthenticatedUser | null;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallback = null,
  showFallback = false,
  user: overrideUser,
}) => {
  const { authenticatedUser } = useAuthenticatedUser();
  const user = overrideUser ?? authenticatedUser;

  const hasAccess = user && allowedRoles.includes(user.role);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (showFallback && fallback) {
    return <>{fallback}</>;
  }

  if (showFallback) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Access Restricted</AlertTitle>
          <AlertDescription>
            Your role ({user?.role || 'unknown'}) doesn't have access to this content.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return null;
};

// Admin only guard
export const AdminOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
  showFallback?: boolean;
}> = ({ children, fallback, showFallback = false }) => (
  <RoleGuard allowedRoles={['admin']} fallback={fallback} showFallback={showFallback}>
    {children}
  </RoleGuard>
);

// Editor and Admin guard
export const EditorAndAdmin: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
  showFallback?: boolean;
}> = ({ children, fallback, showFallback = false }) => (
  <RoleGuard allowedRoles={['admin', 'editor']} fallback={fallback} showFallback={showFallback}>
    {children}
  </RoleGuard>
);

// All roles guard (essentially authenticated users)
export const AuthenticatedOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
  showFallback?: boolean;
}> = ({ children, fallback, showFallback = false }) => (
  <RoleGuard
    allowedRoles={['admin', 'editor', 'viewer']}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </RoleGuard>
);

// Hook for using permissions in components
export const usePermissions = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  return {
    user: authenticatedUser,
    hasPermission: (permission: Permission) => hasPermission(authenticatedUser, permission),
    hasAnyPermission: (permissions: Permission[]) =>
      hasAnyPermission(authenticatedUser, permissions),
    hasAllPermissions: (permissions: Permission[]) =>
      hasAllPermissions(authenticatedUser, permissions),
    isAdmin: authenticatedUser?.role === 'admin',
    isEditor: authenticatedUser?.role === 'editor',
    isViewer: authenticatedUser?.role === 'viewer',
    isAtLeastEditor: authenticatedUser?.role === 'admin' || authenticatedUser?.role === 'editor',
    canPerformWrites:
      authenticatedUser?.role === 'admin' || authenticatedUser?.role === 'editor',
  };
};
